import { ReturnsAndClaimsPermissionsType } from "@redotech/account-common/permissions";
import * as zExt from "@redotech/rpc/ext";
import * as z from "zod";
import {
  ReturnActionEligibilityReturn,
  ReturnActionEligibilityUser,
} from "../returns/return-action-eligibility";

export interface ReturnActionContext {
  return_: ReturnActionEligibilityReturn;
  /** Absent when no user is behind the action: a public API token's call. */
  user: ReturnActionEligibilityUser | undefined;
}

/**
 * One thing a merchant can do to one return. Everything a caller needs to
 * decide whether to offer it, and what input it takes, lives here — the
 * detail page and the bulk bar both read this and nothing else.
 */
export interface ReturnActionDef<P> {
  label: string;
  /**
   * The public API mutation that starts a run of the action over many returns;
   * the public MCP names its tool after it.
   */
  mutation: `${string}Returns`;
  /** ANDed by callers, so an empty list can never mean "everyone". */
  permissions: readonly [
    ReturnsAndClaimsPermissionsType,
    ...ReturnsAndClaimsPermissionsType[],
  ];
  /**
   * No undo. Reset deletes the return and rewrites its orders' flow state,
   * process and reject move money; flag and add tag both round-trip. Surfaces
   * confirm harder for these.
   */
  irreversible?: boolean;
  /** Pure, and the same predicate wherever the action is offered or enforced. */
  isEligible(ctx: ReturnActionContext): boolean;
  payloadSchema: z.ZodType<P>;
  /** Must parse under payloadSchema. Drives the conformance spec. */
  examplePayload: P;
}

/** Pins `P` to the schema so the registry keeps inference. */
export function defineReturnAction<P>(
  def: ReturnActionDef<P>,
): ReturnActionDef<P> {
  return def;
}

/**
 * What doing one action to one return produced. `skipped` is a decision not to
 * act and is not a failure.
 */
export const SkippedOutcomeSchema = z.object({
  status: z.literal("skipped"),
  code: z.string(),
  message: z.string(),
});

export const ReturnActionOutcomeSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("ok"), code: z.string().optional() }),
  SkippedOutcomeSchema,
  z.object({
    status: z.literal("failed"),
    code: z.string(),
    message: z.string(),
  }),
]);
export type ReturnActionOutcome = z.infer<typeof ReturnActionOutcomeSchema>;

/**
 * Where one return stands in a bulk run. The first three are the run's own
 * states; the rest are what the child (or the prepare step) reported.
 */
export const BulkReturnActionItemOutcomeSchema = z.discriminatedUnion(
  "status",
  [
    z.object({ status: z.literal("pending") }),
    z.object({ status: z.literal("running") }),
    // The child was already running for someone else, so this run left it.
    z.object({ status: z.literal("alreadyRunning") }),
    ...ReturnActionOutcomeSchema.options,
  ],
);
export type BulkReturnActionItemOutcome = z.infer<
  typeof BulkReturnActionItemOutcomeSchema
>;

/** A state the run will not change again. */
export type SettledBulkReturnActionOutcome = Exclude<
  BulkReturnActionItemOutcome,
  { status: "pending" } | { status: "running" }
>;
export function isSettledBulkReturnActionOutcome(
  outcome: BulkReturnActionItemOutcome,
): outcome is SettledBulkReturnActionOutcome {
  return outcome.status !== "pending" && outcome.status !== "running";
}

export const BulkReturnActionResultSchema = z.object({
  returnId: zExt.objectId(),
  outcome: BulkReturnActionItemOutcomeSchema,
});
export type BulkReturnActionResult = z.infer<
  typeof BulkReturnActionResultSchema
>;
